<script>
  import { onMount } from "svelte";
  import * as d3 from "d3";
  import { parliamentChart } from "d3-parliament-chart";
  import { computePosition, flip, shift } from "@floating-ui/dom";
  import SeatSummary from "./SeatSummary.svelte";

  export let seatData;
  export let candidates;

  const sections = 1;
  const sectionGap = 22;
  const rowHeight = 22;
  const seatRadius = 8;

  let chartContainer, chart, chartGroup, svg;
  let selectedParties = [];
  let filteredData = [];
  let allParties = [];
  let filteredParties = [];
  let allPartyAmount = [];

  let selectedAge = null;
  let selectedGenders = ["F"];
  let selectedState = "";
  let selectedOccupation = "";

  const bundeslaender = [
    "Baden-Württemberg",
    "Bayern",
    "Berlin",
    "Brandenburg",
    "Bremen",
    "Hamburg",
    "Hessen",
    "Mecklenburg-Vorpommern",
    "Niedersachsen",
    "Nordrhein-Westfalen",
    "Rheinland-Pfalz",
    "Saarland",
    "Sachsen",
    "Sachsen-Anhalt",
    "Schleswig-Holstein",
    "Thüringen",
  ];

  const occupations = [
    "Politik & Verwaltung",
    "Recht & Justiz",
    "Wirtschaft & Finanzen",
    "Wissenschaft & Forschung",
    "Gesundheitswesen",
    "Bildung & Erziehung",
    "Technik & Ingenieurwesen",
    "Medien & Kommunikation",
    "Soziale Arbeit & Beratung",
    "Handwerk & Industrie",
    "Selbstständig & Unternehmer",
    "Sonstige & Diverse",
  ];

  $: if ($seatData.length || selectedAge || selectedGenders || selectedState || selectedOccupation) {
    updateChart();
  }

  function updateChart() {
    if (!chartContainer || $seatData.length === 0) {
      return;
    } else {
      document.querySelectorAll(".tooltip-container").forEach((el) => el.remove());

      allParties = $seatData.map((party) => party.party);
      filteredParties = $seatData.map((party) => ({
        party: party.party,
        party_name: party.party_name,
        seats: 0,
        totalSeats: 0,
      }));

      let totalSeats = 630;
      let allocatedSeats = 0;
      let activeParties = selectedParties.length ? selectedParties : allParties;

      // filteredData = $seatData
      //   .filter((party) => activeParties.includes(party.party) && party.seats > 0)
      //   .map((party) => {
      //     allocatedSeats += party.seats;
      //     return { ...party };
      //   });
      // if (allocatedSeats > totalSeats) {
      //   let scaleFactor = totalSeats / allocatedSeats;
      //   allocatedSeats = 0;

      //   filteredData = filteredData.map((party) => {
      //     let adjustedSeats = Math.floor(party.seats * scaleFactor);
      //     allocatedSeats += adjustedSeats;
      //     return { ...party, seats: adjustedSeats };
      //   });

      //   let remainingSeats = totalSeats - allocatedSeats;
      //   if (remainingSeats > 0) {
      //     let largestParty = filteredData.reduce((max, party) => (party.seats > max.seats ? party : max));
      //     largestParty.seats += remainingSeats;
      //   }
      // }
      // if (allocatedSeats < totalSeats) {
      //   filteredData.push({
      //     seats: totalSeats - allocatedSeats,
      //     color: "var(--dv-grey-700)",
      //     name: "Unbesetzt",
      //   });
      // }

      filteredData = $candidates.sort((a, b) => a.party.localeCompare(b.party));

      filteredData = filteredData.map((el) => {
        el.seats = 1;
        el.color = `var(--dv-${el.party})` || "var(--dv-grey-800)";
        let birthyear = parseInt(el.birthdate.slice(0, 4));
        let candidateAge = 2025 - birthyear;
        if (selectedAge !== null && (candidateAge > selectedAge + 5 || candidateAge < selectedAge - 5)) {
          el.color = "var(--dv-grey-800)";
        }
        if (selectedGenders.indexOf(el.gender) < 0) {
          el.color = "var(--dv-grey-800)";
        }
        if (selectedState && selectedState !== el.state) {
          el.color = "var(--dv-grey-800)";
        }

        if (selectedOccupation && selectedOccupation !== el.occupation_group) {
          el.color = "var(--dv-grey-800)";
        }
        // Update counts in filteredParties based on color
        filteredParties.forEach((party) => {
          party.totalSeats = filteredData.filter((el) => el.party === party.party).length;
          party.seats = filteredData.filter(
            (el) => el.color !== "var(--dv-grey-800)" && el.party === party.party,
          ).length;
        });
        return el;
      });

      d3.select(chartContainer).select("svg").remove();

      const width = 948;
      const height = 475;

      svg = d3.select(chartContainer).append("svg").attr("width", "100%").attr("viewBox", `0 0 ${width} ${height}`);

      // 🛠 Sitzpunkte mit Abgeordneten synchronisieren
      let assignedMembers = [];
      let memberIndex = 0;

      chart = parliamentChart()
        .width(width)
        .sections(sections)
        .sectionGap(sectionGap)
        .rowHeight(rowHeight)
        .seatRadius(seatRadius)
        .aggregatedData(filteredData);

      if (Array.isArray($candidates) && $candidates.length > 0) {
        const usedCandidates = new Set();

        filteredData.forEach((party) => {
          const partyMembers = $candidates.filter((p) => p.party === party.party);
          let partyIndex = 0;

          for (let i = 0; i < party.seats; i++) {
            let candidate = null;

            // Find the next available candidate
            while (partyIndex < partyMembers.length) {
              if (!usedCandidates.has(partyMembers[partyIndex])) {
                candidate = partyMembers[partyIndex];
                usedCandidates.add(candidate);
                break;
              }
              partyIndex++;
            }

            assignedMembers.push(candidate);
          }
        });
      }

      chartGroup = svg.append("g").call(chart);
      if (Array.isArray($candidates) && $candidates.length > 0) {
        const tooltip = d3
          .select("#btw_sitzverteilung_demografie")
          .append("div")
          .attr("class", "tooltip-container")
          .style("position", "absolute")
          .style("opacity", 0)
          .style("pointer-events", "none");

        chartGroup
          .selectAll("circle")
          .each((d, index, nodes) => {
            if (assignedMembers[index]) {
              d.member = assignedMembers[index];
            }
          })
          .on("mouseover", function (event, d) {
            d3.select(this).attr("stroke", "var(--dv-black)").attr("stroke-width", 2);

            if (d.member) {
              tooltip
                .html(
                  `<div class="foto"><img src="${d.member.foto}"/></div>
                  <div class="name">${d.member.first_name} ${d.member.last_name}</div>
               <span style="background-color: var(--dv-${d.member.party})"></span> ${d.member.party_name} · Jahrgang ${d.member.birthdate.slice(0, 4)}<br>
               <div class="small">${d.member.mandate == "direct" ? "Direktmandat" : "Landesliste"}</div>
               ${d.member.constituency}<br>
               <div class="small">Beruf</div>
                ${d.occupation}<br>
               <div class="small">Foto: ${d.member.foto_credits}</div>`,
                )
                .style("opacity", 1)
                .style("visibility", "visible");
            } else {
              tooltip
                .html("Kein Abgeordneter für diesen Sitz gefunden.")
                .style("opacity", 1)
                .style("visibility", "visible");
            }

            computePosition(this, tooltip.node(), {
              placement: "top",
              middleware: [flip(), shift()],
            }).then(({ x, y }) => {
              tooltip.style("left", `${x}px`).style("top", `${y}px`);
            });
          })
          .on("mousemove", function (event) {
            computePosition(this, tooltip.node(), {
              placement: "top",
              middleware: [flip(), shift()],
            }).then(({ x, y }) => {
              tooltip.style("left", `${x}px`).style("top", `${y}px`);
            });
          })
          .on("mouseout", function () {
            d3.select(this).attr("stroke", "none");
            const tooltip = this.dataset.tooltipId;
            if (tooltip) {
              d3.select(tooltip).remove();
              delete this.dataset.tooltipId;
            }
          });

        // Add a global click listener to hide the tooltip when clicking outside the <g class="parliament-chart"> element
        document.addEventListener("click", function (event) {
          const isOutside = !chartGroup.node().contains(event.target) && !tooltip.node().contains(event.target);
          if (isOutside) {
            tooltip.style("opacity", 0).style("visibility", "hidden");
          }
        });
      }
      // svg
      //   .append("line")
      //   .attr("x1", width / 2)
      //   .attr("x2", width / 2)
      //   .attr("y1", 0)
      //   .attr("y2", height / 1.55)
      //   .attr("stroke", "var(--dv-grey-600)")
      //   .attr("stroke-width", 2);
    }
  }
  function toggleParty(partyId) {
    if (selectedParties.includes(partyId)) {
      selectedParties = selectedParties.filter((p) => p !== partyId);
    } else {
      selectedParties = [...selectedParties, partyId];
    }
    updateChart();
  }
  const threshold_Majority = 316;
  $: percentage = (totalSeatsSum / 630) * 100;
  $: totalSeatsSum = filteredParties.reduce((sum, party) => sum + party.seats, 0);
  $: majorityMessage = `${totalSeatsSum} / 630<br />Abgeordneten<br />(${percentage === 0 ? percentage.toFixed(0) : percentage < 1 ? percentage.toFixed(1) : percentage.toFixed(0)}%)`;

  onMount(() => {
    updateChart();
    chartContainer.addEventListener("mouseleave", () => {
      d3.selectAll(".tooltip-container").html("").style("opacity", 0);
    });
    resetAge();
  });

  function resetAge() {
    selectedAge = null;
    updateChart(); // Call updateChart to refresh the data
  }
</script>

<div class="seats-container" bind:this={chartContainer}>
  <div class="majority-indicator">{@html majorityMessage}</div>
</div>
<div class="seat_legend_container">
  <ul id="seat_legend">
    {#each filteredParties as party}
      <li>
        {party.party_name}
        <span style="color: var(--dv-{party.party})">{party.seats}</span><span
          class="percent"
          style="color: var(--dv-{party.party})"
          >({party.totalSeats > 0
            ? (party.seats / party.totalSeats) * 100 === 0
              ? ((party.seats / party.totalSeats) * 100).toFixed(0)
              : (party.seats / party.totalSeats) * 100 < 1
                ? ((party.seats / party.totalSeats) * 100).toFixed(1)
                : ((party.seats / party.totalSeats) * 100).toFixed(0)
            : 0}%)</span
        >
      </li>
    {/each}
  </ul>
  <div class="seat_legend_footer">Anteil in der Fraktion</div>
</div>
<div class="control-container">
  <div class="age-container">
    <div class="label">Alter</div>
    {#if selectedAge === null}
      <div class="age">Alle Altersgruppen</div>
    {:else}
      <div class="age">{selectedAge - 5}-{selectedAge + 5}</div>
    {/if}
    <div class="range">
      <input
        type="range"
        min="23"
        max="80"
        bind:value={selectedAge}
        class="slider {selectedAge === null ? 'disabled' : ''}"
        id="myRange"
      />
    </div>

    <div class="btn">
      <button type="button" on:click={resetAge}>
        {#if selectedAge != null}alle Altersgruppen{/if}
      </button>
    </div>
  </div>
  <div class="gender-container">
    <div class="label">Geschlecht</div>
    <span>
      <input id="gender_w" type="checkbox" bind:group={selectedGenders} value="F" />
      <label for="gender_w">weiblich</label>
    </span>
    <span>
      <input id="gender_m" type="checkbox" bind:group={selectedGenders} value="M" />
      <label for="gender_m">männlich</label>
    </span>
    <span>
      <input id="gender_d" type="checkbox" bind:group={selectedGenders} value="D" />
      <label for="gender_d">divers</label>
    </span>
  </div>
  <div class="state">
    <label class="label" for="bundesland">Bundesland</label>
    <select id="bundesland" bind:value={selectedState}>
      <option value="" selected>Alle</option>
      {#each bundeslaender as state}
        <option value={state}>{state}</option>
      {/each}
    </select>
  </div>
  <div class="state">
    <label class="label" for="profession">Berufsgruppe</label>
    <select id="profession" bind:value={selectedOccupation}>
      <option value="" selected>Alle</option>
      {#each occupations as occupation}
        <option value={occupation}>{occupation}</option>
      {/each}
    </select>
  </div>
</div>

<style>
</style>
